import { useState } from "react";
import { useParams } from "react-router-dom";
import CompanyPageView from "./CompanyPageView";
import useCustomEffect from "../../../hooks/useCustomEffect";
import { useRouteContext } from "../../../context/RouteContext";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";

function CompanyPage() {
  const callEvaApi = useEvaApi();
  const routeContext = useRouteContext();
  const { company } = useParams();
  const [companyData, setCompanyData] = useState({
    fetched: false,
    name: "",
    contact: "",
    contact_details: "",
    license: "basic",
    active: true,
    use_print: false,
    remark: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    type: "default",
  });
  const [submitError, setSubmitError] = useState(null);

  /**
   * initialize the page
   */
  useCustomEffect(() => {
    if (company) {
      fetchCompanyData();
    }
  });

  /**
   * This method will fetch the company data from the API
   */
  async function fetchCompanyData() {
    await callEvaApi(
      new EvaApiCall(`companies/${company}`)
        .setLoadingGroup("company_page_fetch")
        .setOnSuccess((response) => {
          setCompanyData({
            fetched: true,
            name: response.data.data.name,
            contact: response.data.data.contact,
            contact_details: response.data.data.contact_details,
            license: response.data.data.license,
            active: response.data.data.active,
            use_print: response.data.data.use_print,
            remark: response.data.data.remark,
            timezone: response.data.data.timezone,
            type: response.data.data.type,
          });
        })
        .setRedirectOnError(true)
    );
  }

  /**
   * This method will update the name on input change
   * @param {event} e
   */
  function onNameChange(e) {
    setCompanyData({ ...companyData, name: e.target.value });
  }

  /**
   * This method will update the contact on input change
   * @param {event} e
   */
  function onContactChange(e) {
    setCompanyData({ ...companyData, contact: e.target.value });
  }

  /**
   * This method will update the contact details on input change
   * @param {event} e
   */
  function onContactDetailsChange(e) {
    setCompanyData({ ...companyData, contact_details: e.target.value });
  }

  /**
   * This method will handle the license change
   * @param {any} value
   */
  function onLicenseChange(value) {
    setCompanyData({ ...companyData, license: value });
  }

  /**
   * This method will handle the active change
   * @param {any} value
   */
  function onActiveChange(value) {
    setCompanyData({ ...companyData, active: value });
  }

  /**
   * This method will handle the use print change
   * @param {any} value
   */
  function onUsePrintChange(value) {
    setCompanyData({ ...companyData, use_print: value });
  }

  /**
   * This method will handle the remark change
   * @param {event} e
   */
  function onRemarkChange(e) {
    setCompanyData({ ...companyData, remark: e.target.value });
  }

  /**
   * This method will handle the timezone change
   * @param {any} value
   */
  function onTimezoneChange(value) {
    setCompanyData({ ...companyData, timezone: value });
  }

  /**
   * This method will handle the type change
   * @param {any} value
   */
  function onTypeChange(value) {
    setCompanyData({ ...companyData, type: value });
  }

  /**
   * This method will handle the onCancel functionality
   */
  function onCancel() {
    routeContext.back();
  }

  /**
   * This method will submit the form
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall(company ? `companies/${company}` : "companies")
        .setLoadingGroup("company_page_save")
        .setMethod(company ? "PUT" : "POST")
        .setData(companyData)
        .setErrorState(setSubmitError)
        .setRedirectOnSuccess(true)
    );
  }

  return (
    <CompanyPageView
      company={company}
      companyData={companyData}
      onNameChange={onNameChange}
      onContactChange={onContactChange}
      onContactDetailsChange={onContactDetailsChange}
      onLicenseChange={onLicenseChange}
      onActiveChange={onActiveChange}
      onUsePrintChange={onUsePrintChange}
      onRemarkChange={onRemarkChange}
      onTimezoneChange={onTimezoneChange}
      onTypeChange={onTypeChange}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitError={submitError}
    />
  );
}

export default CompanyPage;
